import { Check, Loader2, X } from "lucide-react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { cn } from "~/lib/utils";
import { CohortAccessRequest } from "~/pages/manifold-nextgen/components/cohorts/interfaces/access_requests";

export type ApprovalStreamStatus = "idle" | "streaming" | "done" | "error";

export interface ProgressItem {
  message: string;
  status: "loading" | "done";
}

export interface ExportPanelState {
  requestId: string;
  datasetName: string;
  streamStatus: ApprovalStreamStatus;
  progressItems: ProgressItem[];
  resultDatasetId: string | null;
  resultWarnings: string[];
  streamError: string | null;
}

interface ExportPanelProps {
  request: CohortAccessRequest;
  panel: ExportPanelState;
  onDatasetNameChange: (name: string) => void;
}

export function ExportPanel({ request, panel, onDatasetNameChange }: ExportPanelProps) {
  const isStreaming = panel.streamStatus === "streaming";
  const isDone = panel.streamStatus === "done";
  const showProgress = panel.streamStatus !== "idle";

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Approving this request will grant{" "}
        <span className="font-semibold text-foreground">{request.created_by}</span> access to the
        exported dataset.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor={`dataset-name-${request.id}`} className="font-semibold">
          Dataset name
        </Label>
        <Input
          id={`dataset-name-${request.id}`}
          value={panel.datasetName}
          onChange={(e) => onDatasetNameChange(e.target.value)}
          disabled={isStreaming || isDone}
        />
      </div>

      {showProgress && (
        <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-green-800 dark:text-green-400">
            Export progress
          </p>
          <ul className="space-y-1.5">
            {panel.progressItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                {item.status === "loading" ? (
                  <Loader2
                    className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                ) : (
                  <Check
                    className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-400"
                    aria-hidden
                  />
                )}
                <span
                  className={cn(
                    item.status === "done" ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {item.message}
                </span>
              </li>
            ))}
            {panel.streamStatus === "error" && panel.streamError && (
              <li className="flex items-start gap-2 text-sm">
                <X className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
                <span className="text-destructive">{panel.streamError}</span>
              </li>
            )}
          </ul>
          {isDone && panel.resultWarnings.length > 0 && (
            <ul className="space-y-1">
              {panel.resultWarnings.map((warning, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <X className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
                  <span className="text-amber-700 dark:text-amber-400">{warning}</span>
                </li>
              ))}
            </ul>
          )}
          {isDone && panel.resultDatasetId && (
            <a
              href={`/nextgen/datasets/${panel.resultDatasetId}`}
              className="inline-flex items-center gap-1 rounded-lg border border-green-600 px-3 py-1.5 text-sm font-semibold text-green-700 hover:bg-green-100 dark:text-green-400 dark:border-green-600 dark:hover:bg-green-900/30"
            >
              Open dataset in catalog →
            </a>
          )}
        </div>
      )}
    </div>
  );
}
